import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Modal, FooterButton } from 'Core/components';
import CustomPropTypes from 'Core/prop-types';
import { addMoldMaintenance, editMoldMaintenance, editMoldMaintenanceData } from './store/slice';
import { AppModal } from '../components/app-modals';
import { showToast } from 'Core/redux/toast/slice';
import MoldMaintenanceAddEditForm from './mold-maintenance-add-edit-form';
import { useDocumentUpload } from '../../core/hooks';
import LoadingSpinner from '../../InitialPage/Sidebar/LoadingSpinner';
import { MOLD_MAINTENANCE } from '../../core/constants/moldConstants';
import { Tab, Tabs } from 'Core/components';
import MoldMaintenanceHistory from './mold-maintenance-history';
import { ACTION_MODE } from '../../core/constants/constants';
import { sanitizeUploadKey } from '../../core/components/sanitizeUploadKeys/sanitizeUploadKeys';
import { MOLD_TYPE } from '../../core/constants/moldConstants';
import { MoldService } from '../../service/api/mold-service';
import ConfirmModal from '../components/app-modals/confirm-modal';

const getLoggedInUserDisplayName = () => {
  try {
    return (
      localStorage.getItem('UserName') ||
      localStorage.getItem('userName') ||
      localStorage.getItem('UserId') ||
      ''
    );
  } catch {
    return '';
  }
};

/** Local date+time string (yyyy-MM-ddTHH:mm:ss) aligned with datetime-local / typical DateTime parse. */
const nowDateTimeForMaintenanceApi = () => {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/** Only touches maintenance_PerformedBy / submittedBy (+ optional dates) before saveOrUpdateMoldMaintenance. */
const applyApprovalFieldsForMaintenanceSave = (data, { forCloseRecord = false } = {}) => {
  const next = { ...data };
  const user = getLoggedInUserDisplayName();
  if (forCloseRecord) {
    next.submittedBy = user || '';
    if (next.submittedByDate == null || String(next.submittedByDate).trim() === '') {
      next.submittedByDate = nowDateTimeForMaintenanceApi();
    }
  } else {
    // Omit from payload so the API does not update Submitted By / date on Save (only Close sends them).
    delete next.submittedBy;
    delete next.submittedByDate;
  }
  if (!String(next.maintenance_PerformedBy || '').trim() && user) {
    next.maintenance_PerformedBy = user;
    if (next.maintenance_PerformedBy_Date == null || String(next.maintenance_PerformedBy_Date).trim() === '') {
      next.maintenance_PerformedBy_Date = nowDateTimeForMaintenanceApi();
    }
  }
  return next;
};

// Function to calculate total maintenance hours for API call
const calculateTotalMaintenanceHours = (currentHours, displayTotalHours, maintenanceId, originalTotalHours) => {
  console.log('calculateTotalMaintenanceHours input:', { currentHours, displayTotalHours, maintenanceId, originalTotalHours });
  
  const current = parseFloat(currentHours) || 0;
  const originalTotal = parseFloat(originalTotalHours) || 0;
  
  // Ensure values are within reasonable limits to prevent arithmetic overflow
  // Use much smaller limit to be absolutely safe (likely database column is TINYINT = 255 or SMALLINT = 32,767)
  const maxHours = 10000; // Very conservative limit
  const currentSafe = Math.min(Math.max(current, 0), maxHours);
  const originalTotalSafe = Math.min(Math.max(originalTotal, 0), maxHours);
  
  console.log('calculateTotalMaintenanceHours safe values:', { currentSafe, originalTotalSafe, maxHours });
  
  // For new maintenance (id is 0 or undefined), total equals current hours
  if (!maintenanceId || maintenanceId === 0) {
    console.log('calculateTotalMaintenanceHours result (new):', currentSafe);
    return currentSafe;
  }
  
  // For existing maintenance, add current hours to the original total from API
  const total = currentSafe + originalTotalSafe;
  
  // Ensure total doesn't exceed maximum
  const finalTotal = Math.min(total, maxHours);
  
  console.log('calculateTotalMaintenanceHours result (existing):', finalTotal);
  return finalTotal;
};

const MoldMaintenanceAddEdit = ({ showModal, modalData, onCloseModal }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(modalData || null);
  
  // Debug: Log formData whenever it changes
  console.log('Parent component: formData =', formData);
  console.log('Parent component: formData.moldMaintenanceData =', formData?.moldMaintenanceData);
  console.log('Parent component: formData.moldMaintenanceData?.id =', formData?.moldMaintenanceData?.id);
  console.log('Parent component: formData.moldMaintenanceData?.version =', formData?.moldMaintenanceData?.version);
  console.log('Parent component: formData.moldMaintenanceData?.isClosed =', formData?.moldMaintenanceData?.isClosed);
  console.log('Parent component: modalData =', modalData);
  console.log('Parent component: modalData?.moldMaintenanceData =', modalData?.moldMaintenanceData);
  
  const [dataToSave, setDataToSave] = useState(() => {
    try {
      const initialData = { ...(modalData?.moldMaintenanceData || {}) };
      // Ensure id is set to 0 for new records if not present
      if (initialData.id === undefined || initialData.id === null) {
        initialData.id = 0;
      }
      console.log('Initial dataToSave:', initialData);
      return initialData;
    } catch (error) {
      console.warn('Error initializing dataToSave, using default:', error);
      return { id: 0 };
    }
  });
  
  const [tempFiles, setTempFiles] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [hideMaintenanceTab, setHideMaintenanceTab] = useState(
    Boolean(modalData?.hideMaintenanceTab),
  );
  const [formMode, setFormMode] = useState(
    modalData?.moldMaintenanceData ? ACTION_MODE.VIEW : ACTION_MODE.ADD,
  );
  const [cleanupFunctions, setCleanupFunctions] = useState([]);
  const [uploadedImageSegments, setUploadedImageSegments] = useState([]);
  const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false);

  const { uploadDocuments, loading } = useDocumentUpload();

  const dispatch = useDispatch();
  const isEditMode = Boolean(
    modalData?.moldReceivingData?.toolID || modalData?.moldMaintenanceData?.toolID,
  );


  const onFileUpload = async (documentName, file) => {
    if (file) {
      const docFormData = new FormData();
      docFormData.append('file', file);
      docFormData.append('fileCategoryId', documentName);
      docFormData.append('uploadCategory', sanitizeUploadKey(MOLD_TYPE.MOLD_MAINTENANCE));

      try {
        const result = await uploadDocuments(docFormData);
        if (result && result.relativePath) {
          // Map document field names to the correct API field names
          const fieldMapping = {
            MAINTENANCE_REPORT_DOCUMENT: 'maintenance_Report_Document',
            INSPECTION_CHECKLIST_DOCUMENT: 'inspection_Checklist_Document',
            FUNCTIONAL_TEST_RESULTS_DOCUMENT: 'functionalTest_Results_Document',
            MOLD_PICTURE_BEFORE_MAINTENANCE: 'moldPicture_Before_Maintenance',
            MOLD_PICTURE_AFTER_MAINTENANCE: 'moldPicture_After_Maintenance',
            // Map specific image types to their corresponding fields
            MAINTENANCE_TYPE_PICTURES: 'additionalPicPath1', // Preventive Maintenance Pictures -> additionalPicPath1
            REPAIR_PICTURES: 'additionalPicPath3', // Repair Pictures -> additionalPicPath3
            ADDITIONAL_PICTURE_ONE: 'additionalPicPath1', // Keep existing mapping
            ADDITIONAL_PICTURE_TWO: 'additionalPicPath2', // Breakdown Maintenance Pictures -> additionalPicPath2
            ADDITIONAL_PICTURE_THREE: 'additionalPicPath3', // Keep existing mapping
            // Map Choose File buttons in Maintenance Records section
            'MOLD_PICTURE_BEFORE_MAINTENANCE_file': 'moldPicture_Before_Maintenance',
            'MOLD_PICTURE_AFTER_MAINTENANCE_file': 'moldPicture_After_Maintenance',
            'ADDITIONAL_PICTURE_ONE_file': 'additionalPicPath1',
            'ADDITIONAL_PICTURE_TWO_file': 'additionalPicPath2',
            'ADDITIONAL_PICTURE_THREE_file': 'additionalPicPath3',
            'ADDITIONAL_PICTURE_THREE_file_4': 'additionalPicPath4', // Mold Modification Pictures
          };

          const apiFieldName = fieldMapping[documentName] || documentName;
          // Update the correct field in dataToSave and child form
          const updated = { ...dataToSave, [apiFieldName]: result.relativePath };
          setDataToSave(updated);
          onChangesMoldMaintForm(updated);
        }
      } catch (error) {
        console.error('File upload failed:', error);
      }
    }
  };
  
  const onChangesMoldMaintForm = data => {
    console.log('onChangesMoldMaintForm called with data:', data);
    console.log('onChangesMoldMaintForm - data.id:', data.id);
    console.log('onChangesMoldMaintForm - data.maintenanceTypes:', data.maintenanceTypes);
    console.log('onChangesMoldMaintForm - current dataToSave.id:', dataToSave.id);
    console.log('onChangesMoldMaintForm - current dataToSave.maintenanceTypes:', dataToSave.maintenanceTypes);
    
    const updatedData = {
      ...dataToSave, // Keep existing data including document fields
      ...data, // Override with new form data
    };
    console.log('Updated dataToSave:', updatedData);
    console.log('Updated dataToSave.id:', updatedData.id);
    console.log('Updated dataToSave.maintenanceTypes:', updatedData.maintenanceTypes);
    setDataToSave(updatedData);
  };

  const onMoldMaintHistoryActionClick = (data, mode) => {
    setFormMode(mode); // NEW LINE
    if (mode == ACTION_MODE.ADD) {
      const newMaintenanceData = {
        id: 0,
        toolID: modalData?.moldReceivingData?.toolID,
        moldInfoID: modalData?.moldReceivingData?.moldInfoID,
      };
      setFormData(
        modalData
          ? {
              ...modalData,
              moldMaintenanceData: newMaintenanceData,
            }
          : null,
      );
      setDataToSave(newMaintenanceData);
    } else {
      // Normalize maintenanceID to id for editing
      let normalizedData = { ...data };
      if (normalizedData.maintenanceID && !normalizedData.id) {
        normalizedData.id = normalizedData.maintenanceID;
      }
      delete normalizedData.maintenanceID;

      setFormData(prev => ({
        ...prev,
        moldMaintenanceData: {
          ...normalizedData,
          isClosed: normalizedData.version === 'CLOSED', // Add this flag locally
        },
      }));
      setDataToSave({
        ...normalizedData,
        isClosed: normalizedData.version === 'CLOSED',
      });
    }
    setHideMaintenanceTab(false);
    setActiveTabIndex(0);
  };

  const handleTabChange = newTabIndex => {
    if (activeTabIndex === 0 && newTabIndex !== 0) {
      setFormData(modalData ? { ...modalData } : null);
    }

    setActiveTabIndex(newTabIndex);
  };

  // Function to add cleanup function to the list
  const addCleanupFunction = (cleanupFn) => {
    if (typeof cleanupFn === 'function') {
      setCleanupFunctions(prev => [...prev, cleanupFn]);
    }
  };

  // Function to track uploaded image segments
  const trackUploadedImageSegment = (segment, record, imageUrl = '', title = '', description = '') => {
    setUploadedImageSegments(prev => [...prev, { 
      segment, 
      record, 
      imageUrl, 
      title, 
      description 
    }]);
  };

  // Function to handle cleanup when form is closed without submit
  const handleFormClose = async () => {
    // Call all cleanup functions
    for (const cleanupFn of cleanupFunctions) {
      if (typeof cleanupFn === 'function') {
        try {
          await cleanupFn();
        } catch (error) {
          console.error('Error during cleanup:', error);
        }
      }
    }
    
    // Clear cleanup functions
    setCleanupFunctions([]);
  };

  const clearDeepLinkFromUrl = () => {
    const params = new URLSearchParams(location.search);
    if (params.has('toolId') || params.has('maintenanceId') || params.has('moldInfoId')) {
      navigate('/mold-maintenance', { replace: true });
    }
  };

  const handleClose = async () => {
    // Perform cleanup if there are any cleanup functions
    if (cleanupFunctions.length > 0) {
      await handleFormClose();
    }
    
    // Clear uploaded segments tracking
    setUploadedImageSegments([]);
    
    // Clear any pending image segments from sessionStorage
    sessionStorage.removeItem('pendingImageSegments');
    
    onCloseModal();
    clearDeepLinkFromUrl();
  };

  // Handle Close Maintenance Record - Show confirmation dialog
  const handleCloseMaintenanceRecord = () => {
    // Show confirmation dialog
    setShowCloseConfirmModal(true);
  };

  // Handle Confirm Close Maintenance Record - Save with CLOSED version
  const handleConfirmCloseMaintenanceRecord = async () => {
    console.log('=== CLOSE MAINTENANCE RECORD (Parent) - Confirmed ===');
    
    // Close the confirmation modal
    setShowCloseConfirmModal(false);
    
    try {
      const updatedData = applyApprovalFieldsForMaintenanceSave(
        { ...dataToSave, version: 'CLOSED' },
        { forCloseRecord: true },
      );
      const filteredData = filterDataForAPI(updatedData);
      
      console.log('Saving maintenance record with CLOSED version:', filteredData);
      
      // Call saveOrUpdateMoldMaintenance API with version: 'CLOSED'
      let maintenanceResponse;
      if (dataToSave?.id == 0) {
        // For new records
        maintenanceResponse = await MoldService.saveUpdateMaintenance(filteredData);
      } else {
        // For existing records
        maintenanceResponse = await MoldService.saveUpdateMaintenance(filteredData);
      }
      
      if (maintenanceResponse && (maintenanceResponse.response || maintenanceResponse.ID || maintenanceResponse.id)) {
        const maintenanceId = maintenanceResponse.ID || maintenanceResponse.id || maintenanceResponse.response?.maintenanceID || maintenanceResponse.response?.id;
        
        // Update local state with CLOSED version
        const finalData = {
          ...updatedData,
          id: maintenanceId || updatedData.id,
          version: 'CLOSED'
        };
        
        setDataToSave(finalData);
        setFormData(prev => ({
          ...prev,
          moldMaintenanceData: {
            ...prev.moldMaintenanceData,
            ...finalData,
            version: 'CLOSED',
            isClosed: true
          },
        }));
        
        // Show success toast message
        dispatch(showToast({
          message: 'Maintenance record has been closed successfully. The form is now read-only.',
          type: 'success'
        }));
      }
      
    } catch (error) {
      console.error('Error closing maintenance record:', error);
      dispatch(showToast({
        message: 'Failed to close maintenance record. Please try again.',
        type: 'danger'
      }));
    }
  };

  const body = () => {
    const historyTab = (
      <Tab title="Mold Maintenance History">
        <MoldMaintenanceHistory
          onActionClick={onMoldMaintHistoryActionClick}
          onCloseModal={handleClose}
          data={formData}
        />
      </Tab>
    );

    return (
      <div className="row">
        {isEditMode && (
          hideMaintenanceTab ? (
            <Tabs activeIndex={0}>{historyTab}</Tabs>
          ) : (
            <Tabs activeIndex={activeTabIndex} onTabChange={handleTabChange}>
              <Tab title="Edit Mold Maintenance">
                <MoldMaintenanceAddEditForm
                  data={formData}
                  onChanges={onChangesMoldMaintForm}
                  onFileUpload={onFileUpload}
                  isDisabled={
                    formData?.moldMaintenanceData?.isClosed ||
                    formData?.moldMaintenanceData?.version === 'CLOSED'
                  }
                  onFormClose={addCleanupFunction}
                  onImageSegmentUploaded={trackUploadedImageSegment}
                  filterDataForAPI={data =>
                    filterDataForAPI(
                      applyApprovalFieldsForMaintenanceSave(data, {
                        forCloseRecord: false,
                      }),
                    )
                  }
                  dataToSave={dataToSave}
                />
              </Tab>
              {historyTab}
            </Tabs>
          )
        )}
      </div>
    );
  };

  const valid = () => {
    console.log('Validation check - dataToSave:', dataToSave);
    
    // For new records, id can be 0, but moldInfoID is required
    if (dataToSave?.moldInfoID === '' || dataToSave?.moldInfoID === undefined) {
      console.log('Validation failed: moldInfoID is missing');
      dispatch(
        showToast({
          message: 'Please fill all required fields',
          type: 'danger',
        }),
      );
      return false;
    }
    
    // Check if maintenanceTypes is selected
    if (!dataToSave?.maintenanceTypes || dataToSave?.maintenanceTypes.length === 0) {
      console.log('Validation failed: maintenanceTypes is missing or empty');
      dispatch(
        showToast({
          message: 'Please select a maintenance type',
          type: 'danger',
        }),
      );
      return false;
    }
    
    console.log('Validation passed');
    return true;
  };

  // Function to filter data to only include fields supported by the updated API
  const filterDataForAPI = (data) => {
    console.log('filterDataForAPI input data:', data);
    console.log('filterDataForAPI - id field:', data.id);
    console.log('filterDataForAPI - maintenanceTypes field:', data.maintenanceTypes);
      
      const {
        // Keep only the fields that are still in the API
        id,
        toolID,
        toolId, // Handle both casing variations
        moldInfoID,
        moldInfoId, // Handle both casing variations
        totalCyclesSinceLastMaint,
        totalCyclesToDate,
        lastMaintenanceDate,
        maintenanceIntervalCycles,
        currentMaintenanceDate,
        currentMaintenanceHours,
        totalMaintenanceHours,
        originalTotalMaintenanceHours,
        cleaning,
        spray,
        comments1,
        inspection,
        comments2,
        lubrication,
        comments3,
        componentReplacement,
        componentsReplaced,
        comments4,
        repair,
        detailsofRepair,
        functionalTest,
        comments5,
        maintenance_Report_Document,
        inspection_Checklist_Document,
        functionalTest_Results_Document,
        additionalComments,
        maintenance_PerformedBy,
        quality_Control_ApprovedBy,
        engineering_ApprovedBy,
        submittedBy,
        maintenance_PerformedBy_Date,
        quality_Control_ApprovedBy_Date,
        engineering_ApprovedBy_Date,
        submittedByDate,
        version,
        maintenanceTypes,
        // Extract image-related fields to include in API payload
        moldPicture_Before_Maintenance,
        moldPicture_After_Maintenance,
        functionalTestPicture,
        additionalPicPath1,
        additionalPicPath2,
        additionalPicPath3,
        additionalPicPath4,
        additionalLabel,
        uploadedImages,
        ...rest
      } = data;

      // Helper function to safely convert numeric values
      const safeNumericString = (value, maxValue = 10000) => {
        const num = parseFloat(value) || 0;
        const safeNum = Math.min(Math.max(num, 0), maxValue);
        return String(safeNum);
      };

      const result = {
        ID: id || 0, // Ensure ID is never null/undefined - this should be the actual maintenance ID
        ToolID: toolID || toolId, // Map to correct API field name
        MoldInfoID: moldInfoID || moldInfoId, // Map to correct API field name
        TotalCyclesSinceLastMaint: safeNumericString(totalCyclesSinceLastMaint, 10000),
        TotalCyclesToDate: totalCyclesToDate ? safeNumericString(totalCyclesToDate, 10000) : null,
        LastMaintenanceDate: lastMaintenanceDate,
        MaintenanceIntervalCycles: safeNumericString(maintenanceIntervalCycles, 10000),
        CurrentMaintenanceDate: currentMaintenanceDate,
        CurrentMaintenanceHours: safeNumericString(currentMaintenanceHours, 10000),
        TotalMaintenanceHours: calculateTotalMaintenanceHours(currentMaintenanceHours, totalMaintenanceHours, id, originalTotalMaintenanceHours),
        Cleaning: cleaning,
        Spray: spray,
        Comments1: comments1,
        Inspection: inspection,
        Comments2: comments2,
        Lubrication: lubrication,
        Comments3: comments3,
        ComponentReplacement: componentReplacement,
        ComponentsReplaced: componentsReplaced,
        Comments4: comments4,
        Repair: repair,
        DetailsofRepair: detailsofRepair,
        FunctionalTest: functionalTest,
        Comments5: comments5,
        Maintenance_Report_Document: maintenance_Report_Document,
        Inspection_Checklist_Document: inspection_Checklist_Document,
        FunctionalTest_Results_Document: functionalTest_Results_Document,
        AdditionalComments: additionalComments,
        Maintenance_PerformedBy: maintenance_PerformedBy,
        Quality_Control_ApprovedBy: quality_Control_ApprovedBy,
        Engineering_ApprovedBy: engineering_ApprovedBy,
        Maintenance_PerformedBy_Date: maintenance_PerformedBy_Date,
        Quality_Control_ApprovedBy_Date: quality_Control_ApprovedBy_Date,
        Engineering_ApprovedBy_Date: engineering_ApprovedBy_Date,
        Version: version,
        MaintenanceType: maintenanceTypes && maintenanceTypes.length > 0 
          ? maintenanceTypes 
          : [],
        // Include image-related fields with relative paths
        MoldPicture_Before_Maintenance: moldPicture_Before_Maintenance,
        MoldPicture_After_Maintenance: moldPicture_After_Maintenance,
        AdditionalPicPath1: additionalPicPath1,
        AdditionalPicPath2: additionalPicPath2,
        AdditionalPicPath3: additionalPicPath3,
        AdditionalPicPath4: additionalPicPath4,
      };

      // Only include when present (Save omits them; Close Maintenance Record sets them).
      if (submittedBy !== undefined) {
        result.SubmittedBy = submittedBy;
        // SP parameter @MaintennaceSubmittedBy — same value as SubmittedBy for this flow
        result.MaintennaceSubmittedBy = submittedBy;
      }
      if (submittedByDate !== undefined) {
        result.SubmittedByDate = submittedByDate;
      }

      console.log('filterDataForAPI output - MaintenanceType:', result.MaintenanceType);
      console.log('filterDataForAPI output - ID:', result.ID);
      return result;
  };

  const footer = () => {

    const saveMaintenance = async (shouldClose = false) => {
      console.log('saveMaintenance called, dataToSave:', dataToSave);
      console.log('saveMaintenance - dataToSave.maintenanceTypes:', dataToSave?.maintenanceTypes);
      
      if (!valid()) {
        console.log('Validation failed');
        return;
      }
      
      try {
        const payloadBase = applyApprovalFieldsForMaintenanceSave({ ...dataToSave }, {
          forCloseRecord: false,
        });
        const filteredData = filterDataForAPI(payloadBase);
        console.log('Filtered data to save:', filteredData);
        console.log('Parent: ID field in payload:', filteredData.ID);
        console.log('Parent: MaintenanceType field in payload:', filteredData.MaintenanceType);
        console.log('Parent: dataToSave.id:', dataToSave?.id);
        console.log('Parent: dataToSave.maintenanceTypes:', dataToSave?.maintenanceTypes);
        console.log('Parent: dataToSave:', dataToSave);
        console.log('Parent: modalData?.moldMaintenanceData:', modalData?.moldMaintenanceData);
        
        let maintenanceResponse;
        if (dataToSave?.id == 0) {
          // For new records, use the direct API call
          console.log('Calling saveUpdateMaintenance for new record');
          try {
            maintenanceResponse = await MoldService.saveUpdateMaintenance(filteredData);
            console.log('Maintenance response:', maintenanceResponse);
          } catch (apiError) {
            console.error('API Error details:', apiError);
            throw new Error(`API Error: ${apiError.message || apiError}`);
          }
        } else {
          // For existing records, use the direct API call to avoid Redux listener side effects
          console.log('Calling saveUpdateMaintenance for existing record');
          try {
            maintenanceResponse = await MoldService.saveUpdateMaintenance(filteredData);
            console.log('Maintenance response:', maintenanceResponse);
          } catch (apiError) {
            console.error('API Error details:', apiError);
            throw new Error(`API Error: ${apiError.message || apiError}`);
          }
        }
        
        if (maintenanceResponse && (maintenanceResponse.response || maintenanceResponse.ID || maintenanceResponse.id)) {
          const maintenanceId = maintenanceResponse.ID || maintenanceResponse.id || maintenanceResponse.response?.maintenanceID || maintenanceResponse.response?.id;

          setDataToSave(prev => ({
            ...prev,
            ...(maintenanceId && dataToSave?.id == 0 ? { id: maintenanceId } : {}),
            maintenance_PerformedBy: payloadBase.maintenance_PerformedBy,
            maintenance_PerformedBy_Date: payloadBase.maintenance_PerformedBy_Date,
            ...(Object.prototype.hasOwnProperty.call(payloadBase, 'submittedBy')
              ? { submittedBy: payloadBase.submittedBy, submittedByDate: payloadBase.submittedByDate }
              : { submittedBy: '', submittedByDate: null }),
          }));
          setFormData(prev => ({
            ...prev,
            moldMaintenanceData: {
              ...prev.moldMaintenanceData,
              ...(maintenanceId && dataToSave?.id == 0 ? { id: maintenanceId } : {}),
              maintenance_PerformedBy: payloadBase.maintenance_PerformedBy,
              maintenance_PerformedBy_Date: payloadBase.maintenance_PerformedBy_Date,
              ...(Object.prototype.hasOwnProperty.call(payloadBase, 'submittedBy')
                ? { submittedBy: payloadBase.submittedBy, submittedByDate: payloadBase.submittedByDate }
                : { submittedBy: '', submittedByDate: null }),
            },
          }));
          
          // Show success message
          dispatch(
            showToast({
              message: 'Mold Maintenance saved successfully',
              type: 'success',
            }),
          );
          
          // Close modal only if shouldClose is true
          if (shouldClose) {
            onCloseModal();
            clearDeepLinkFromUrl();
          }
        }
      } catch (error) {
        console.error('Failed to save mold maintenance:', error);
        dispatch(
          showToast({
            message: error.message || 'Failed to save mold maintenance',
            type: 'danger',
          }),
        );
      }
    };

    const onSave = () => saveMaintenance(false); // Save button - stays on page
    const onSaveAndClose = () => saveMaintenance(true); // Save & Close button - closes modal

    const isClosed = formData?.moldMaintenanceData?.version === 'CLOSED';

    if (hideMaintenanceTab || activeTabIndex !== 0) return null;
    return (
      <div className="row">
        <div className="col-lg-8"></div>
        <div className="col-lg-4">
          <div className="modal-footer-btn d-flex justify-content-end align-items-center">
            {/* ✅ Hide Cancel + Submit if isClosed  */}
            {!isClosed && (
              <>
                <button
                  className="btn btn-secondary me-2"
                  onClick={handleClose}
                  style={{ height: '46px', minWidth: '100px' }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success me-2"
                  onClick={onSave}
                  style={{ height: '46px', minWidth: '100px' }}
                >
                  Save
                </button>
                <button
                  className="btn btn-primary"
                  onClick={onSaveAndClose}
                  style={{ height: '46px', minWidth: '120px' }}
                >
                  Save & Close
                </button>
              </>
            )}
            {/* {formMode === ACTION_MODE.VIEW && !formData?.moldMaintenanceData?.isClosed && ( */}

            {/* ✅ Show Close Maintenance Record button only if it's not already closed */}
            {formMode === ACTION_MODE.VIEW && !isClosed && (
              <button
                className="btn btn-danger me-1"
                style={{ height: '46px', minWidth: '200px' }}
                onClick={handleCloseMaintenanceRecord}
              >
                Close Maintenance Record
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        id="mold-maintenance-modal"
        show={showModal}
        size="xl"
        title={isEditMode ? 'Edit Mold Maintenance' : 'Add Mold Maintenance'}
        body={body}
        footer={footer}
        handleClose={handleClose}
      />
      {loading && <LoadingSpinner />}

      {/* Confirmation Modal for Close Maintenance Record */}
      <ConfirmModal
        showModal={showCloseConfirmModal}
        title="Close Maintenance Record"
        message="Do you really want to close this maintenance? All changes will be saved and the form will become read-only."
        onCloseModal={() => setShowCloseConfirmModal(false)}
        onConfirmModal={handleConfirmCloseMaintenanceRecord}
      />

    </>
  );
};

MoldMaintenanceAddEdit.propTypes = {
  showModal: PropTypes.bool,
  modalData: PropTypes.shape(CustomPropTypes.MoldMaintenance),
  onCloseModal: PropTypes.func,
};

export default AppModal(MoldMaintenanceAddEdit);


